
import { Capacitor } from '@capacitor/core';

/**
 * Determine if the current platform is native (Android or iOS)
 */
export const isNativePlatform = (): boolean => {
  return Capacitor.isNativePlatform();
};

/**
 * Get the platform name (ios, android, web)
 */
export const getPlatformName = (): string => {
  return Capacitor.getPlatform();
};

/**
 * Generate universal/deep links for the app
 * @param path The path to create a link for
 * @param params Optional query parameters
 */
export const generateAppLink = (path: string, params?: Record<string, string>): string => {
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  // Format: https://go.pulsefiction.app/path?params
  let url = `https://go.pulsefiction.app${normalizedPath}`;
  
  // Add query parameters if they exist
  if (params && Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      searchParams.append(key, value);
    });
    url += `?${searchParams.toString()}`;
  }
  
  return url;
};

/**
 * Generate custom URL scheme for the app
 * @param path The path to create a link for (without leading slash for deep links)
 * @param params Optional query parameters
 */
export const generateCustomSchemeLink = (path: string, params?: Record<string, string>): string => {
  // Remove leading slash for deep link format
  const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
  
  // Format: pulsefiction://path?params
  let url = `pulsefiction://${normalizedPath}`;
  
  // Add query parameters if they exist
  if (params && Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      searchParams.append(key, value);
    });
    url += `?${searchParams.toString()}`;
  }
  
  return url;
};

/**
 * Try to open the app with a specific path
 * @param path The path to open in the app
 * @param params Optional query parameters
 */
export const openInApp = (path: string, params?: Record<string, string>): void => {
  // First try with the custom URL scheme
  const customSchemeUrl = generateCustomSchemeLink(path, params);
  
  // Use a hidden iframe to try to open the app
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = customSchemeUrl;
  document.body.appendChild(iframe);
  
  // Clean up the iframe after a delay
  setTimeout(() => {
    document.body.removeChild(iframe);
  }, 500);
};

/**
 * Check if the current URL is a deep link redirect
 * This helps identify if we arrived via a deep link
 */
export const isDeepLinkRedirect = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has('deep_link');
};

/**
 * Get app store link based on platform
 */
export const getAppStoreLink = (): string => {
  // Replace these with actual app store links when available
  const platform = getPlatformName();
  
  if (platform === 'ios') {
    return 'https://apps.apple.com/app/pulsefiction'; // Update with real App Store link
  } else {
    return 'https://play.google.com/store/apps/details?id=app.pulsefiction.pulse'; // Update with real Play Store link
  }
};
